import { useEffect } from 'react';
import {
  AI_MODELS,
  TISSUE_MODEL_SHORT_NAME,
} from 'components/AITools/constants/common';
import _ from 'lodash';
import {
  handleRegularModel,
  handleTissueModel,
} from 'components/AITools/utilities/common';
import {
  addCurrentSlideAIModelData,
  aiModelsData,
} from 'store/slices/aiToolsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyGetSlideMLDataQuery } from 'store/apis/slidesApi';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { QUANTIFIFCATION_ERROR } from 'constants/errorMessages';
import { getRequestedModelsShortNames } from 'components/AITools/utilities/quantification';

const useSetAiModals = ({ options, opacityOptions }) => {
  const [getSlideMLData] = useLazyGetSlideMLDataQuery();
  const { showError } = useSnackbar();

  const findModel = (results, shortName) =>
    results.find((result) => result[shortName])?.[shortName]?.coordinates ||
    null;

  const setAiModels = async ({ slide, aiModels, handleAIModels }) => {
    const requestedModelsShortNames = getRequestedModelsShortNames({
      options,
      opacityOptions,
      aiModels,
    });

    if (requestedModelsShortNames.length) {
      const promises = requestedModelsShortNames.map(
        (requestedModelShortName) =>
          getSlideMLData({
            slideUUID: slide.uuid,
            model: requestedModelShortName,
          }).unwrap(),
      );

      await Promise.all(promises)
        .then((results) => {
          const tissueModelCoordinates =
            aiModels[TISSUE_MODEL_SHORT_NAME] ||
            handleTissueModel(
              _.cloneDeep(findModel(results, TISSUE_MODEL_SHORT_NAME)),
            );

          requestedModelsShortNames.forEach((shortModelName) => {
            if (!aiModels[shortModelName]) {
              const modelCoordinates = findModel(results, shortModelName);

              if (modelCoordinates) {
                const _coordinates = _.cloneDeep(modelCoordinates);

                const handledCoordinates =
                  shortModelName === TISSUE_MODEL_SHORT_NAME
                    ? handleTissueModel(_coordinates)
                    : handleRegularModel(_coordinates, tissueModelCoordinates);

                handleAIModels({
                  modelName: shortModelName,
                  data: handledCoordinates,
                });
              }
            }
          });
        })
        .catch(() => showError(QUANTIFIFCATION_ERROR));
    }
  };

  return setAiModels;
};

export const useModelQuantifications = (
  model,
  slide,
  isExpanded,
  opacityOptions,
) => {
  const setAiModels = useSetAiModals({
    options: model?.options,
    opacityOptions,
  });
  const aiModels = useSelector(aiModelsData)[slide.uuid] || {};
  const dispatch = useDispatch();

  useEffect(() => {
    const isQuantificationRequired = model?.id !== AI_MODELS.autoQC;

    if (isExpanded && model?.options && isQuantificationRequired) {
      const handleAIModels = ({ modelName, data }) => {
        dispatch(addCurrentSlideAIModelData({ slide, modelName, data }));
      };

      setAiModels({ slide, aiModels, handleAIModels });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, slide.uuid, opacityOptions]);

  return { aiModels };
};

export const useSlidesModelQuantification = ({
  model,
  slides,
  opacityOptions,
}) => {
  const setAiModels = useSetAiModals({
    options: model?.options,
    opacityOptions,
  });

  const getSlidesModelQuantification = async () => {
    const isQuantificationRequired = model?.id !== AI_MODELS.autoQC;
    const aiModels = {};

    if (model?.options && isQuantificationRequired) {
      // eslint-disable-next-line no-restricted-syntax
      for (const slide of slides) {
        const handleAIModels = ({ modelName, data }) => {
          aiModels[slide.uuid] = {
            ...(aiModels[slide.uuid] || {}),
            [modelName]: data,
          };
        };

        // eslint-disable-next-line no-await-in-loop
        await setAiModels({
          slide,
          aiModels: aiModels[slide.uuid] || {},
          handleAIModels,
        });
      }
    }

    return aiModels;
  };

  return getSlidesModelQuantification;
};
